import mainStyle from '../mainStyle.module.scss';

import { useState } from 'react';
import { cloneDeep } from 'lodash';

import { useTheme } from '@mui/material/styles';
import { Box, Button, Checkbox, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

import { ReactComponent as CheckBoxDefault } from 'assets/images/icons/checkbox_default.svg';
import { ReactComponent as CheckClearIcon } from 'assets/images/icons/checkbox_clear.svg';
import { ReactComponent as CheckBoxFilled } from 'assets/images/icons/checkbox_fiilled.svg';

// Components
import { SolidButton } from 'components/buttons/SolidButton';
import { OutlineButton } from 'components/buttons/OutlineButton';
import ModalContainer from 'components/ModalContainer';

// Const
import { predefinedOuiList } from '../const';

const ImportModal = ({ openImportModal, setOpenImportModal, showEditGeneral, formData, setFormData }) => {
  const theme = useTheme();

  const [list, setList] = useState(cloneDeep(predefinedOuiList));

  const ouiBindingListCount = formData?.ouiBindingList?.length || 0;
  const allChecked = list?.length > 0 && list.every(row => row.checked);
  const hasChecked = list?.some(row => row.checked);
  const isSelectedCount = list?.filter(item => item.checked)?.length || 0;
  const isIndeterminate = hasChecked && !allChecked;

  const checkImportBtnDisabled = () => !showEditGeneral || !hasChecked || ouiBindingListCount + isSelectedCount > 10;

  const handleChangeCheckbox = (key, isChecked, index) => {
    if (key !== 'CHECK_ALL') {
      setList(prev => prev.map((item, idx) => (idx === index ? { ...item, checked: isChecked } : item)));
    } else {
      setList(prev => prev.map(item => ({ ...item, checked: isChecked })));
    }
  };

  const handleClickCancelBtn = () => {
    setOpenImportModal(false);
    setList(cloneDeep(predefinedOuiList));
  };

  const handleClickAddBtn = () => {
    // Handle the logic for adding the selected OUIs to the main list
    const selectedItems = list.filter(item => item.checked);

    // normalize address/mask from colon-separated to dash-separated (AA:BB -> AA-BB)
    const normalizedSelected = selectedItems.map(item => ({
      ...item,
      address: item.address ? item.address.replace(/:/g, '-') : item.address,
      mask: item.mask ? item.mask.replace(/:/g, '-') : item.mask,
    }));

    const updatedOuiBindingList = [...(formData?.ouiBindingList || []), ...normalizedSelected.map(item => ({ ...item, checked: false }))];

    setFormData(prev => ({
      ...prev,
      ouiBindingList: updatedOuiBindingList,
    }));

    // Reset the list and close the modal
    setList(cloneDeep(predefinedOuiList));
    setOpenImportModal(false);
  };

  return (
    <ModalContainer display={openImportModal} modalWidth="850px">
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '0 0 10px 20px',
        }}
      >
        <Typography
          sx={{
            color: theme.palette.primary.main,
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.customFontSize[5],
            fontWeight: theme.typography.customFontWeight[3],
          }}
        >
          Import Predefined OUI
        </Typography>
        <Button
          onClick={handleClickCancelBtn}
          sx={{
            backgroundColor: theme.palette.primary.transparent,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: theme.palette.primary.transparent,
            },
          }}
        >
          <CloseIcon />
        </Button>
      </Box>
      <TableContainer>
        <Table>
          <TableHead
            sx={{
              '& th': {
                color: '#aaaaaa',
                fontSize: '15px',
                fontWeight: '500',
              },
              borderTop: 'none',
              borderColor: '1px solid #d5d5d5',
            }}
          >
            <TableRow sx={{ backgroundColor: '#ffffff', '& .MuiTableCell-root': { padding: '2px 6px 2px 10px' } }}>
              <TableCell>
                <Typography className={mainStyle['table-header']}>#</Typography>
              </TableCell>
              {showEditGeneral && (
                <TableCell
                  sx={{
                    width: '56px',
                    padding: '4px 8px',
                    '&:not(:last-of-type)::after': {
                      display: 'none',
                    },
                  }}
                >
                  <Checkbox
                    icon={<CheckBoxDefault style={{ width: '24px', height: '24px' }} />}
                    indeterminateIcon={<CheckClearIcon style={{ width: '24px', height: '24px' }} />}
                    checkedIcon={<CheckBoxFilled style={{ width: '24px', height: '24px' }} />}
                    indeterminate={isIndeterminate}
                    checked={allChecked}
                    onChange={(_, checked) => handleChangeCheckbox('CHECK_ALL', checked, null)}
                    sx={{
                      '&:hover': { backgroundColor: 'transparent' },
                    }}
                  />
                </TableCell>
              )}
              <TableCell>
                <Typography className={mainStyle['table-header']}>OUI Address</Typography>
              </TableCell>
              <TableCell>
                <Typography className={mainStyle['table-header']}>Mask</Typography>
              </TableCell>
              <TableCell>
                <Typography className={mainStyle['table-header']}>Description</Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list?.length > 0 ? (
              list.map((item, index) => (
                <TableRow
                  key={`oui-binding-table-row-${index}`}
                  sx={{
                    backgroundColor: index % 2 === 1 ? theme.palette.primary.whiteSecound : theme.palette.primary.white,
                    '& .MuiTableCell-root': { border: 'none', padding: '2px 6px 2px 10px' },
                  }}
                >
                  <TableCell>{index + 1}</TableCell>
                  {showEditGeneral && (
                    <TableCell sx={{ width: '56px', padding: '4px 8px' }}>
                      <Checkbox
                        icon={<CheckBoxDefault style={{ width: '24px', height: '24px' }} />}
                        checkedIcon={<CheckBoxFilled style={{ width: '24px', height: '24px' }} />}
                        checked={item.checked}
                        onClick={e => e.stopPropagation()}
                        onChange={e => handleChangeCheckbox('NOT_CHECK_ALL', e.target.checked, index)}
                        sx={{
                          '&:hover': { backgroundColor: 'transparent' },
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>{item.address}</TableCell>
                  <TableCell>{item.mask}</TableCell>
                  <TableCell>{item.description}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow
                sx={{
                  '& .MuiTableCell-root': { border: 'none' },
                }}
              >
                <TableCell colSpan={4} align="center">
                  <Typography
                    sx={{
                      fontSize: theme.typography.customFontSize[2],
                      color: theme.palette.primary.black,
                    }}
                  >
                    No entry in the table
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
        <Typography
          sx={{
            fontSize: theme.typography.customFontSize[2],
            color: theme.palette.primary.black,
          }}
        >
          <Typography component="span" sx={{ fontSize: '15px', fontWeight: theme.typography.customFontWeight[3] }}>
            {isSelectedCount}
          </Typography>
          {` / 10 Selected`}
        </Typography>
        {ouiBindingListCount + isSelectedCount > 10 && (
          <Typography sx={{ fontSize: theme.typography.customFontSize[2], color: theme.palette.primary.redPrimary }}>
            The OUI Binding List can only have a maximum of 10 entries.
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          marginTop: '50px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <OutlineButton sx={{ marginRight: '20px' }} width="100px" height="32px" onClick={handleClickCancelBtn}>
          Cancel
        </OutlineButton>
        <SolidButton width="100px" height="32px" onClick={handleClickAddBtn} disabled={checkImportBtnDisabled()}>
          Import
        </SolidButton>
      </Box>
    </ModalContainer>
  );
};

export default ImportModal;
